import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import { baseUrl, baseimgUrl } from "../Univ";

function EditProduct() {

  const { id } = useParams();
  const navigate = useNavigate();

  // ---------------- STATES ----------------
  const [form, setForm] = useState({
    name: "",
    category: "",
    price: "",
    discount_price: "",
    stock: "",
    description: "",

    colors: [],
    size: "",
    dimensions: "",

    warranty_value: "",
    warranty_unit: "month",
    warranty_description: "",

    is_active: true
  });

  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [colorInput, setColorInput] = useState("");

  // ---------------- FETCH DATA ----------------
  useEffect(() => {

    axios.get(baseUrl + `products/${id}/`)
      .then(res => {

        setForm({
          name: res.data.name,
          category: res.data.category,
          price: res.data.price,
          discount_price: res.data.discount_price,
          stock: res.data.stock,
          description: res.data.description,

          colors: res.data.colors || [],
          size: res.data.size || "",
          dimensions: res.data.dimensions || "",

          warranty_value: res.data.warranty_value || "",
          warranty_unit: res.data.warranty_unit || "month",
          warranty_description: res.data.warranty_description || "",

          is_active: res.data.is_active
        });

        setExistingImages(res.data.images || []);
      });

    axios.get(baseUrl + "categories/")
      .then(res => setCategories(res.data));

  }, [id]);

  // ---------------- HANDLERS ----------------
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImages = (e) => {
    setImages([...e.target.files]);
  };

  // -------- COLORS ----------
  const addColor = () => {
    if (colorInput.trim() !== "") {
      setForm({
        ...form,
        colors: [...form.colors, colorInput]
      });
      setColorInput("");
    }
  };

  const removeColor = (index) => {
    setForm({
      ...form,
      colors: form.colors.filter((_, i) => i !== index)
    });
  };

  // ---------------- UPDATE ----------------
  const updateProduct = async (e) => {
    e.preventDefault();

    try {

      const productData = new FormData();

      productData.append("name", form.name);
      productData.append("category", form.category);
      productData.append("price", form.price);
      productData.append("discount_price", form.discount_price);
      productData.append("stock", form.stock);
      productData.append("description", form.description);

      productData.append("colors", JSON.stringify(form.colors));
      productData.append("size", form.size);
      productData.append("dimensions", form.dimensions);
      productData.append("warranty_value", form.warranty_value);
      productData.append("warranty_unit", form.warranty_unit);
      productData.append("warranty_description", form.warranty_description);

      await axios.put(
        baseUrl + `update-product/${id}/`,
        productData
      );

      // ---------- Upload New Images ----------
      if (images.length > 0) {

        const imgData = new FormData();
        imgData.append("product", id);
        imgData.append("main_image_index", mainImageIndex);

        images.forEach(img => {
          imgData.append("images", img);
        });

        await axios.post(
          baseUrl + "add-product-images/",
          imgData
        );
      }

      Swal.fire({
        title: "Product Updated Successfully",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });

      navigate("/seller/products");

    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Update Failed", "error");
    }
  };

  // ---------------- UI ----------------
  return (
    <div className="container mt-4">

      <h3>Edit Product</h3>

      <form onSubmit={updateProduct}>

        {/* BASIC */}
        <input
          name="name"
          className="form-control mb-2"
          placeholder="Product Name"
          value={form.name}
          onChange={handleChange}
        />

<select
  name="category"
  className="form-control mb-2"
  value={form.category?.id || form.category || ""}
  onChange={handleChange}
  required
>
  <option value="">Select Category</option>
  {categories.map((cat) => {
    // Build breadcrumb path
    let path = [cat.name];
    let p = categories.find((c) => c.id === cat.parent);
    while (p) {
      path.unshift(p.name);
      p = categories.find((c) => c.id === p.parent);
    }
    return (
      <option key={cat.id} value={cat.id}>
        {path.join(" › ")}
      </option>
    );
  })}
</select>

        <input
          name="price"
          className="form-control mb-2"
          placeholder="Price"
          value={form.price}
          onChange={handleChange}
        />

        <input
          name="discount_price"
          className="form-control mb-2"
          placeholder="Discount Price"
          value={form.discount_price}
          onChange={handleChange}
        />

        <input
          name="stock"
          className="form-control mb-2"
          placeholder="Stock"
          value={form.stock}
          onChange={handleChange}
        />

        <textarea
          name="description"
          className="form-control mb-2"
          placeholder="Description"
          value={form.description}
          onChange={handleChange}
        />

        {/* COLORS */}
        <label>Colors</label>
        <div className="d-flex mb-2">
          <input
            value={colorInput}
            onChange={(e)=>setColorInput(e.target.value)}
            className="form-control"
          />
          <button type="button" onClick={addColor} className="btn btn-primary ms-2">
            Add
          </button>
        </div>

        {form.colors.map((c,i)=>(
          <span key={i} className="badge bg-success me-1">
            {c}
            <span
              style={{cursor:"pointer"}}
              onClick={()=>removeColor(i)}
            > ✖</span>
          </span>
        ))}

        {/* SIZE & DIMENSIONS */}
        <input
          name="size"
          className="form-control mb-2 mt-2"
          placeholder="Size (S, M, XL)"
          value={form.size}
          onChange={handleChange}
        />

        <input
          name="dimensions"
          className="form-control mb-2"
          placeholder="Dimensions (10x5x3 cm)"
          value={form.dimensions}
          onChange={handleChange}
        />

        {/* WARRANTY */}
        <div className="row">

          <div className="col-md-4">
            <input
              name="warranty_value"
              className="form-control mb-2"
              placeholder="Warranty Number"
              value={form.warranty_value}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <select
              name="warranty_unit"
              className="form-control mb-2"
              value={form.warranty_unit}
              onChange={handleChange}
            >
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
          </div>

        </div>

        <textarea
          name="warranty_description"
          className="form-control mb-2"
          placeholder="Warranty Description"
          value={form.warranty_description}
          onChange={handleChange}
        />

        {/* EXISTING IMAGES */}
        <label>Existing Images</label>
        <div className="d-flex gap-2 mb-2">
          {existingImages.map((img,i)=>(
            <img key={i} src={img.image} height="80" />
          ))}
        </div>

        {/* NEW IMAGES */}
        <label>Upload New Images</label>
        <input
          type="file"
          multiple
          className="form-control mb-2"
          onChange={handleImages}
        />

        <input
          type="number"
          className="form-control mb-2"
          value={mainImageIndex}
          onChange={(e)=>setMainImageIndex(e.target.value)}
          placeholder="Main Image Index"
        />

        <button className="btn btn-primary w-100">
          Update Product
        </button>

      </form>

    </div>
  );
}

export default EditProduct;
